<template>
  <app-content>
    <b-container fluid class="analysis-processing">
      <b-row v-if="shouldShowPendingLotsAmount" align-h="center">
        <b-alert
          variant="warning"
          class="text-center"
          show
          v-html="pendingAmountMessage"
        />
      </b-row>
      <b-row class="no-internal-print">
        <button-panel
          :last-load="lastLoad"
          @refresh="onRefreshButtonClick"
          @show-modal="handleShowHelpModal"
        />
      </b-row>
      <b-row no-gutters class="dropzone__title no-internal-print">
        <dropzone
          :input-accept="allowedFiles"
          :title="$t('actions.import-files')"
          @input="newFilesAdded"
        />
      </b-row>
      <help-modal :showModal="showHelpModal" @show-modal="handleShowHelpModal" />
      <b-row class="mt-5">
        <analysis-processing-table
          :loading="loading"
          v-model="processingItems"
          :total-items="totalItems"
          :per-page="perPage"
          :current-page.sync="currentPage"
          :current-uploads="uploadsIds"
          @update:currentPage="paging"
          @abort-upload="abortUpload"
          @refresh-list="refreshList"
        />
      </b-row>
    </b-container>
  </app-content>
</template>

<script>
import AnalysisProcessingService, {
  UPLOAD_ALLOWED_FILE_TYPES,
} from '@/domain/services/installation/engineering/analysisProcessing';
import loadingMixin from '@/services/loadingMixin';
import Dropzone from '@/components/Dropzone';
import Table from '@/domain/components/generalPanel/analysisProcessing/Table';
import HelpModal from '@/domain/components/generalPanel/analysisProcessing/helpModal';
import ButtonPanel from '@/domain/components/generalPanel/analysisProcessing/ButtonPanel';
import { getNow } from '@/services/dateTime/datetimeService';

export default {
  name: 'AnalysisProcessingIndex',
  mixins: [loadingMixin],
  components: {
    Dropzone,
    ButtonPanel,
    HelpModal,
    'analysis-processing-table': Table,
  },
  data: vm => ({
    timer: null,
    interval: 10000,
    showLogModal: false,
    currentPage: 1,
    totalItems: null,
    perPage: 10,
    uploadManager: vm.createUploadManager(),
    fileList: [],
    lastLoad: null,
    processingItems: [],
    hasUploadError: false,
    amountOfPendingLots: null,
    allowedFiles: UPLOAD_ALLOWED_FILE_TYPES,
    showHelpModal: false,
  }),
  methods: {
    handleShowHelpModal() {
      this.showHelpModal = !this.showHelpModal;
    },
    paging() {
      this.refreshList();
    },
    newFilesAdded(files) {
      if (this.fileList.length === 0) {
        this.hasUploadError = false;
      }

      this.fileList = [
        ...this.fileList,
        ...files.map(file => ({
          file,
          id: null,
          percentageSent: 0,
        })),
      ];
      this.uploadManager.appendFile(...files);
    },
    onRefreshButtonClick({ wait }) {
      wait(this.automaticRefresh());
    },
    async automaticRefresh() {
      this.clearTimer();
      await this.refreshList();
      this.timer = setTimeout(this.automaticRefresh, this.interval);
    },
    clearTimer() {
      if (this.timer) {
        clearTimeout(this.timer);
      }
    },
    refreshList() {
      this.startLoading();
      return AnalysisProcessingService.getProcessingList(
        this.getRequestFilter(),
      )
        .then(pagedResponse => {
          this.processingItems = [];
          this.processingItems = pagedResponse.data;
          this.totalItems = pagedResponse.total;
          this.lastLoad = getNow();
          this.amountOfPendingLots = pagedResponse.summary.amountOfPendingLots;
        })
        .finally(() => {
          this.endLoading();
        });
    },
    createUploadManager() {
      return AnalysisProcessingService.createUploadManager({
        listeners: {
          'retrieved-file-id': this.onRetrievedFileId,
          'on-progress': this.onUploadProgress,
          'on-success': this.onUploadSuccess,
          'on-error': this.onUploadError,
        },
        listenersScope: this,
      });
    },
    onRetrievedFileId({ file, id }) {
      const fileData = this.getFileData(file);
      this.$set(fileData, 'id', id);

      this.currentPage = 1;
      this.refreshList();
    },
    onUploadProgress({ file, percentageSent }) {
      const fileData = this.getFileData(file);
      this.$set(fileData, 'percentageSent', percentageSent);
    },
    onUploadSuccess(file) {
      this.removeFileAndRefreshListIfNeeded(file);

      if (this.fileList.length === 0 && !this.hasUploadError) {
        this.$pushAlert({ code: 'MS024' });
      }
    },
    onUploadError(file) {
      this.removeFileAndRefreshListIfNeeded(file);
      this.hasUploadError = true;
    },
    async removeFileAndRefreshListIfNeeded(file) {
      const fileData = this.getFileData(file);

      this.removeFileData(fileData);

      if (this.isShowingUploadFile(fileData)) {
        await this.refreshList();
      }
    },
    isShowingUploadFile(fileData) {
      return this.processingItems.some(
        lot => lot.source.fileId === fileData.id,
      );
    },
    abortUpload(fileId) {
      this.uploadManager.stop(this.getFileDataById(fileId).file);
      this.$pushAlert({ code: 'MS020' });
    },
    getFileData(file) {
      return this.fileList.find(fileData => fileData.file === file);
    },
    removeFileData(fileData) {
      this.fileList.splice(this.fileList.indexOf(fileData), 1);
    },
    getFileDataById(fileId) {
      return this.fileList.find(({ id }) => id === fileId);
    },
    getRequestFilter() {
      return {
        page: this.currentPage,
        pageSize: this.perPage,
      };
    },
  },
  computed: {
    uploadsIds() {
      return this.fileList.map(({ id }) => id);
    },
    shouldShowPendingLotsAmount() {
      return this.amountOfPendingLots > 0;
    },
    pendingAmountMessage() {
      return this.$t('engineering.analysis-processing.pending-lots-alert', {
        amountOfPendingLots: this.amountOfPendingLots,
      });
    },
  },
  created() {
    this.automaticRefresh();
  },
  beforeDestroy() {
    this.clearTimer();
  },
};
</script>
